import React, { useMemo } from 'react';
import { LeadRecord, OpportunityRecord, LeadSource } from '../../types/throughput';
import { Card, CardHeader, CardTitle } from '../ui/Card';
interface LeadSourceBreakdownProps {
  leads: LeadRecord[];
  opportunities: OpportunityRecord[];
  onSourceClick: (source: LeadSource) => void;
  selectedSource: LeadSource | null;
}
type SourceRow = {
  source: LeadSource;
  claimed: number;
  converted: number;
  funded: number;
  pullThrough: number;
};
const SOURCE_COLORS: Record<LeadSource, string> = {
  Web: '#7C3AED',
  Referral: '#04844B',
  Partner: '#06B6D4',
  Phone: '#FF5D2D',
  Email: '#3B82F6',
  Other: '#706E6B'
};
export function LeadSourceBreakdown({
  leads,
  opportunities,
  onSourceClick,
  selectedSource
}: LeadSourceBreakdownProps) {
  const rows: SourceRow[] = useMemo(() => {
    const sources: LeadSource[] = ['Web', 'Referral', 'Partner', 'Phone', 'Email', 'Other'];
    return sources.map(source => {
      const sourceLeads = leads.filter(l => l.source === source);
      const sourceLeadIds = new Set(sourceLeads.map(l => l.id));
      const sourceOpps = opportunities.filter(o => sourceLeadIds.has(o.leadId));
      const funded = sourceOpps.filter(o => o.fundedDate).length;
      const claimed = sourceLeads.length;
      return {
        source,
        claimed,
        converted: sourceOpps.length,
        funded,
        pullThrough: claimed > 0 ? funded / claimed * 100 : 0
      };
    }).filter(r => r.claimed > 0).sort((a, b) => b.funded - a.funded);
  }, [leads, opportunities]);
  const totalClaimed = rows.reduce((sum, r) => sum + r.claimed, 0);
  if (rows.length === 0) return null;
  return <Card className="mb-8 p-0 overflow-hidden" noPadding data-id="element-58">
      <CardHeader bordered className="!mb-0" data-id="element-59">
        <CardTitle data-id="element-60">By Lead Source</CardTitle>
      </CardHeader>

      <div className="overflow-x-auto" data-id="element-61">
        <table className="w-full text-sm" data-id="element-62">
          <thead className="text-xs uppercase tracking-wide text-[#706E6B] bg-[#FAFAFA] border-b border-[#E5E5E5]" data-id="element-63">
            <tr data-id="element-64">
              <th className="px-6 py-3.5 font-medium text-left" data-id="element-65">Source</th>
              <th className="px-6 py-3.5 font-medium text-right" data-id="element-66">Claimed</th>
              <th className="px-6 py-3.5 font-medium text-right" data-id="element-67">Converted</th>
              <th className="px-6 py-3.5 font-medium text-right" data-id="element-68">Funded</th>
              <th className="px-6 py-3.5 font-medium text-right" data-id="element-69">
                Pull-through
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E5]" data-id="element-70">
            {rows.map(row => {
            const isSelected = selectedSource === row.source;
            return <tr key={row.source} className={`cursor-pointer transition-colors hover:bg-[#F5F3FF] group ${isSelected ? 'bg-[#F5F3FF]' : 'bg-white'}`} onClick={() => onSourceClick(row.source)} data-id="element-71">
                  <td className="px-6 py-3.5" data-id="element-72">
                    <div className="flex items-center gap-2.5" data-id="element-73">
                      <div className="w-2 h-2 rounded-full shrink-0" style={{
                    backgroundColor: SOURCE_COLORS[row.source]
                  }} data-id="element-74" />
                      <span className={`font-medium group-hover:text-[#7C3AED] transition-colors ${isSelected ? 'text-[#7C3AED]' : 'text-[#181818]'}`} data-id="element-75">
                        {row.source}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3.5 text-right tabular-nums text-[#706E6B]" data-id="element-76">
                    {row.claimed.toLocaleString()}
                  </td>
                  <td className="px-6 py-3.5 text-right tabular-nums text-[#706E6B]" data-id="element-77">
                    {row.converted.toLocaleString()}
                  </td>
                  <td className="px-6 py-3.5 text-right tabular-nums font-medium text-[#181818]" data-id="element-78">
                    {row.funded.toLocaleString()}
                  </td>
                  <td className="px-6 py-3.5 text-right tabular-nums text-[#706E6B]" data-id="element-79">
                    {row.pullThrough.toFixed(1)}%
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>

      {/* Distribution bar */}
      <div className="px-6 py-3.5 border-t border-[#E5E5E5] bg-[#FAFAFA]" data-id="element-80">
        <div className="flex h-1.5 rounded-full overflow-hidden bg-[#E5E5E5]" data-id="element-81">
          {rows.map(row => {
          const pct = totalClaimed > 0 ? row.claimed / totalClaimed * 100 : 0;
          if (pct < 0.5) return null;
          return <div key={row.source} className="h-full transition-all duration-300" style={{
            width: `${pct}%`,
            backgroundColor: SOURCE_COLORS[row.source]
          }} data-id="element-82" />;
        })}
        </div>
      </div>
    </Card>;
}